import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Moon, Sun, ArrowRight, MessageCircle } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';

export const GlassNav = () => {
  const { isDark, toggleTheme } = useTheme();
  return (
    <nav
      data-testid="lhf-nav"
      className={`fixed top-0 inset-x-0 z-50 backdrop-blur-xl border-b ${
        isDark ? 'bg-slate-950/70 border-slate-800/60' : 'bg-white/70 border-slate-200/60'
      }`}
    >
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link
          to="/"
          data-testid="lhf-nav-home"
          className={`text-sm sm:text-lg tracking-[0.15em] sm:tracking-[0.2em] font-thin whitespace-nowrap ${isDark ? 'text-white' : 'text-slate-900'}`}
          style={{ fontFamily: "'Montserrat', sans-serif" }}
        >
          DION HAIR CLINIC
        </Link>
        <div className="flex items-center gap-3">
          <button
            onClick={toggleTheme}
            data-testid="lhf-theme-toggle"
            className={`p-2 rounded-full transition-colors ${
              isDark ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'
            }`}
            aria-label="Theme wechseln"
          >
            {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <a
            href="/#kontakt"
            data-testid="lhf-nav-cta"
            className="px-5 py-2 rounded-full bg-gradient-to-r from-[#7BA7C2] to-[#9BBFD9] text-white text-sm font-medium hover:opacity-90 transition-opacity"
          >
            Beratung buchen
          </a>
        </div>
      </div>
    </nav>
  );
};

const FAQS = [
  {
    q: 'Ist die Long Hair FUE wirklich komplett ohne Rasur?',
    a: 'Ja. Sowohl der Entnahme- als auch der Empfängerbereich bleiben unrasiert. Die Grafts werden in voller Haarlänge entnommen und eingesetzt — der behandelte Bereich ist sofort optisch kaschiert.',
  },
  {
    q: 'Für wen ist die Methode geeignet?',
    a: 'Besonders für Menschen mit kleineren bis mittleren kahlen Bereichen, die keine Ausfallzeit akzeptieren können und maximale Diskretion wünschen. Ob sie für Sie optimal ist, klären wir in der kostenlosen Analyse.',
  },
  {
    q: 'Warum ist die Long Hair FUE teurer als die klassische FUE?',
    a: 'Die Entnahme und das Einsetzen der Grafts in voller Länge ist deutlich zeitintensiver und erfordert besondere Erfahrung. Der Aufpreis beträgt 3.000€ zum Saphir-Basis-Paket.',
  },
  {
    q: 'Sehe ich sofort das Endergebnis?',
    a: 'Optisch ja — der Bereich ist direkt kaschiert und Sie sehen die neue Haarlinie in voller Länge. Biologisch fallen die verpflanzten Haare nach einigen Wochen zunächst aus (Shock Loss) und wachsen ab dem 3.–4. Monat dauerhaft nach. Das finale Ergebnis zeigt sich nach ca. 12 Monaten.',
  },
  {
    q: 'Kann ich die Behandlung in Raten zahlen?',
    a: 'Ja, über unseren Partner Medipay ab 53€/Monat — die ersten 6 Monate zinsfrei, Laufzeiten bis 72 Monate.',
  },
];

export const FaqSection = () => {
  const { isDark } = useTheme();
  return (
    <section
      data-testid="lhf-faq"
      className={`py-24 md:py-32 px-6 ${isDark ? 'bg-slate-900' : 'bg-white'}`}
    >
      <div className="max-w-3xl mx-auto">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className={`text-4xl sm:text-5xl font-extralight text-center mb-14 ${
            isDark ? 'text-white' : 'text-slate-900'
          }`}
          style={{ fontFamily: "'Montserrat', sans-serif" }}
        >
          Häufige <span className="text-[#7BA7C2]">Fragen</span>
        </motion.h2>
        <Accordion type="single" collapsible className="space-y-4">
          {FAQS.map((f, i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              data-testid={`lhf-faq-item-${i}`}
              className={`rounded-2xl border px-6 ${
                isDark ? 'border-slate-800 bg-slate-950/60' : 'border-slate-200 bg-slate-50/80'
              }`}
            >
              <AccordionTrigger
                data-testid={`lhf-faq-trigger-${i}`}
                className={`text-left text-base hover:no-underline ${isDark ? 'text-white' : 'text-slate-900'}`}
              >
                {f.q}
              </AccordionTrigger>
              <AccordionContent className={`text-sm leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <p className={`mt-8 text-center text-sm ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
          Mehr Details im Ratgeber:{' '}
          <Link
            to="/blog/long-hair-fue-ohne-rasur"
            data-testid="lhf-blog-link"
            className="text-[#7BA7C2] hover:underline"
          >
            Long Hair FUE — der komplette Artikel
          </Link>
        </p>
      </div>
    </section>
  );
};

export const FinalCta = () => {
  const { isDark } = useTheme();
  return (
    <section
      data-testid="lhf-final-cta"
      className={`relative py-28 md:py-40 px-6 overflow-hidden ${isDark ? 'bg-slate-950' : 'bg-slate-50'}`}
    >
      <div
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 60% 50% at 50% 50%, #7BA7C2 0%, transparent 70%)',
        }}
      />
      <div className="relative max-w-3xl mx-auto text-center">
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className={`text-4xl sm:text-5xl lg:text-6xl font-extralight leading-tight ${
            isDark ? 'text-white' : 'text-slate-900'
          }`}
          style={{ fontFamily: "'Montserrat', sans-serif" }}
        >
          Volles Haar.
          <br />
          <span className="bg-gradient-to-r from-[#7BA7C2] to-[#9BBFD9] bg-clip-text text-transparent">
            Ohne dass es jemand merkt.
          </span>
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className={`mt-6 text-base md:text-lg font-light ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
        >
          Lassen Sie sich kostenlos und ehrlich beraten — wir sagen Ihnen auch,
          wenn eine andere Methode besser zu Ihnen passt.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.35 }}
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <a
            href="/#kontakt"
            data-testid="lhf-cta-consultation"
            className="group px-8 py-4 rounded-full bg-gradient-to-r from-[#7BA7C2] to-[#9BBFD9] text-white font-medium inline-flex items-center gap-2 hover:shadow-2xl hover:shadow-[#7BA7C2]/40 transition-shadow"
          >
            Kostenlose Beratung
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
          <a
            href="/preisrechner"
            data-testid="lhf-cta-calculator"
            className={`px-8 py-4 rounded-full border font-medium transition-colors ${
              isDark
                ? 'border-slate-700 text-slate-200 hover:border-[#7BA7C2] hover:text-white'
                : 'border-slate-300 text-slate-700 hover:border-[#7BA7C2] hover:text-slate-900'
            }`}
          >
            Kosten berechnen
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export const FloatingCta = () => {
  const { isDark } = useTheme();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > window.innerHeight * 0.8);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.a
          href="/#kontakt"
          data-testid="lhf-floating-cta"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.4 }}
          className={`fixed bottom-6 right-6 z-50 inline-flex items-center gap-2 px-6 py-3.5 rounded-full backdrop-blur-xl border text-sm font-medium shadow-2xl shadow-[#7BA7C2]/30 ${
            isDark
              ? 'bg-slate-950/70 border-[#7BA7C2]/40 text-white'
              : 'bg-white/80 border-[#7BA7C2]/50 text-slate-900'
          }`}
        >
          <MessageCircle className="w-4 h-4 text-[#7BA7C2]" />
          Kostenlose Beratung
        </motion.a>
      )}
    </AnimatePresence>
  );
};
